import React, { useEffect, useState } from 'react'
import Modal from '@/components/Modal'
import Image from 'next/image'

interface ShopMedia {
  type: 'IMAGE' | 'VIDEO'
  id: number
  thumbnail?: string
  url: string
}

const StoreTourMedia = ({
  media,
  width = 64,
  height = 64
}: {
  media?: ShopMedia[]
  width?: string | number
  height?: string | number
}): JSX.Element | null => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const sortedMedia = [
    ...(media?.filter((item) => item?.type === 'VIDEO') ?? []),
    ...(media?.filter((item) => item?.type === 'IMAGE') ?? [])
  ]

  const visibleMedia = sortedMedia?.slice(0, 5)
  const hasMoreMedia = sortedMedia?.length > 5
  const remainingCount = sortedMedia?.length - 4

  const selectedMedia =
    selectedIndex != null
      ? sortedMedia[selectedIndex]
      : null

  useEffect(() => {
    if (selectedIndex == null) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()

        setSelectedIndex((currentIndex) => {
          if (currentIndex == null) return currentIndex

          return currentIndex === 0
            ? sortedMedia?.length - 1
            : currentIndex - 1
        })
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault()

        setSelectedIndex((currentIndex) => {
          if (currentIndex == null) return currentIndex

          return currentIndex === sortedMedia?.length - 1
            ? 0
            : currentIndex + 1
        })
      }

      if (event.key === 'Escape') {
        setSelectedIndex(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedIndex, sortedMedia?.length])

  if (media == null || media?.length === 0) {
    return null
  }

  const handleMediaClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ): void => {
    event.preventDefault()
    event.stopPropagation()

    setSelectedIndex(index)
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closePreview = () => {
    setSelectedIndex(null)
  }

  const handlePrevious = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault()
    event.stopPropagation()

    if (selectedIndex == null) return

    setSelectedIndex(
      selectedIndex === 0
        ? sortedMedia?.length - 1
        : selectedIndex - 1
    )
  }

  const handleNext = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault()
    event.stopPropagation()

    if (selectedIndex == null) return

    setSelectedIndex(
      selectedIndex === sortedMedia?.length - 1
        ? 0
        : selectedIndex + 1
    )
  }

  return (
    <>
      <div
        className="flex gap-2 mt-3"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        {visibleMedia?.map((item, index) => {
          const imageUrl =
            item?.type === 'VIDEO'
              ? item?.thumbnail
              : item?.url

          if (imageUrl == null || imageUrl === '') {
            return null
          }

          const showMoreCount =
            hasMoreMedia && index === 4

          return (
            <button
              type="button"
              key={`${item?.type}-${item?.id}-${index}`}
              onClick={(event) => {
                handleMediaClick(event, index)
              }}
              className="relative flex-none overflow-hidden rounded-[20px] border-0 p-0 supports-[corner-shape:squircle]:[corner-shape:squircle] "
              style={{
                width,
                height
              }}
            >
              <Image
                src={imageUrl}
                alt="Store tour"
                width={Number(width)}
                height={Number(height)}
                className="w-full h-full object-cover"
              />

              {/* Video icon */}
              {item?.type === 'VIDEO' && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
                    <span className="text-white text-[11px] ml-[2px]">
                      ▶
                    </span>
                  </div>
                </div>
              )}

              {showMoreCount && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center pointer-events-none">
                  <span className="text-white font-bold text-[18px]">
                    +{remainingCount}
                  </span>
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Store Tour Preview */}
      {selectedMedia != null && (
        <Modal
          show={true}
          onHide={closePreview}
          bodyStyle={{
            top: '50%',
            right: '50%',
            transform: 'translate(50%, -50%)',
            width: '90%',
            maxWidth: '700px',
            background: 'transparent'
          }}
          size="md"
        >
          <div
            className="relative flex items-center justify-center bg-black rounded-[16px] overflow-hidden"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
          >

            {/* Left button */}
            {sortedMedia?.length > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center border-0"
              >
                <span className="text-[24px]">
                  ‹
                </span>
              </button>
            )}

            {selectedMedia?.type === 'VIDEO'
              ? (
                <video
                  key={selectedMedia?.id}
                  src={selectedMedia?.url}
                  controls
                  autoPlay
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full max-h-[80vh] object-contain"
                />
                )
              : (
                <Image
                  src={selectedMedia?.url}
                  alt="Store tour preview"
                  width={700}
                  height={700}
                  className="w-full max-h-[80vh] object-contain"
                />
                )}

            {/* Right button */}
            {sortedMedia?.length > 1 && (
              <button
                type="button"
                onClick={handleNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center border-0"
              >
                <span className="text-[24px]">
                  ›
                </span>
              </button>
            )}

            {/* Counter */}
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[12px] px-3 py-1 rounded-full">
              {(selectedIndex ?? 0) + 1} / {sortedMedia?.length}
            </div>

          </div>
        </Modal>
      )}
    </>
  )
}

export default StoreTourMedia
